import React, { useState } from 'react';
import {
  Container,
  Button,
  Table,
  Alert,
  Spinner,
  Card,
  Row,
  Col,
  Badge
} from 'react-bootstrap';
import { Upload, FileSpreadsheet, CheckCircle, Clock } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

const PatternAnalysis = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/pattern/analyze`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze file');
      }

      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <Container fluid className="py-4">
      <div className="mb-4">
        <h1 className="mb-2">Pattern Middle Mile Analysis</h1>
        <p className="text-muted">
          Upload a Pattern middle mile report (XLSX) to analyze MM# breakdown and costs
        </p>
      </div>

      {/* Upload Section */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <FileSpreadsheet size={40} className="text-primary" />
                <div className="flex-grow-1">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="form-control"
                    disabled={loading}
                  />
                  {file && (
                    <small className="text-muted mt-1 d-block">
                      Selected: {file.name}
                    </small>
                  )}
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button
                variant="primary"
                size="lg"
                onClick={handleUpload}
                disabled={!file || loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Upload size={18} className="me-2" />
                    Analyze Report
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <strong>Error:</strong> {error}
        </Alert>
      )}

      {/* Results Section */}
      {results && (
        <>
          {/* Summary Cards */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="border-primary">
                <Card.Body>
                  <div className="text-muted small mb-1">Total MM Shipments</div>
                  <div className="h3 mb-0">{results.totalMMShipments}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-success">
                <Card.Body>
                  <div className="text-muted small mb-1">Completed</div>
                  <div className="h3 mb-0 text-success">
                    {results.summary.totalCompleted}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-warning">
                <Card.Body>
                  <div className="text-muted small mb-1">In Progress</div>
                  <div className="h3 mb-0 text-warning">
                    {results.summary.totalInProgress}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-info">
                <Card.Body>
                  <div className="text-muted small mb-1">Grand Total</div>
                  <div className="h5 mb-0">
                    {formatCurrency(results.summary.grandTotalUSD)}
                  </div>
                  <div className="small text-muted">
                    {formatCurrency(results.summary.grandTotalCAD, 'CAD')}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Exchange Rate Info */}
          <Alert variant="info" className="mb-4">
            <strong>Exchange Rate:</strong> 1 USD = {results.exchangeRate.toFixed(4)} CAD
          </Alert>

          {/* Detailed Table */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">MM Shipment Details</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <Table striped hover responsive className="mb-0">
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                    <tr>
                      <th>MM ID</th>
                      <th>Customer</th>
                      <th>Client Name</th>
                      <th>Status</th>
                      <th className="text-center">Total Boxes</th>
                      <th className="text-center">Processed</th>
                      <th className="text-center">Unprocessed</th>
                      <th className="text-center">Progress</th>
                      <th className="text-end">Total USD</th>
                      <th className="text-end">Total CAD</th>
                      <th>Amazon FCs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.data.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{item.mmId}</strong>
                        </td>
                        <td>{item.customerName}</td>
                        <td>
                          {item.clientName ? (
                            <span className="badge bg-info text-dark">{item.clientName}</span>
                          ) : (
                            <span className="text-muted small">Unknown</span>
                          )}
                        </td>
                        <td>
                          {item.status === 'Completed' ? (
                            <Badge bg="success" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                              <CheckCircle size={14} />
                              Completed
                            </Badge>
                          ) : (
                            <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                              <Clock size={14} />
                              In Progress
                            </Badge>
                          )}
                        </td>
                        <td className="text-center">
                          <strong>{item.totalBoxes}</strong>
                        </td>
                        <td className="text-center text-success">
                          {item.processedBoxes}
                        </td>
                        <td className="text-center text-warning">
                          {item.unprocessedBoxes}
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-2">
                            <div className="progress flex-grow-1" style={{ height: '20px', minWidth: '80px' }}>
                              <div
                                className={`progress-bar ${
                                  item.completionPercentage === 100
                                    ? 'bg-success'
                                    : 'bg-warning'
                                }`}
                                role="progressbar"
                                style={{ width: `${item.completionPercentage}%` }}
                                aria-valuenow={item.completionPercentage}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                {item.completionPercentage}%
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-end">
                          <strong>{formatCurrency(item.totalChargeUSD)}</strong>
                        </td>
                        <td className="text-end">
                          <span className="text-muted">
                            {formatCurrency(item.totalChargeCAD, 'CAD')}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {item.amazonFCs || 'N/A'}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Empty State */}
      {!results && !loading && !error && (
        <Card className="text-center py-5">
          <Card.Body>
            <FileSpreadsheet size={64} className="text-muted mb-3" />
            <h5 className="text-muted">No data yet</h5>
            <p className="text-muted">
              Upload a Pattern middle mile report to get started
            </p>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default PatternAnalysis;
